import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { PeAuthService } from '../../../../core/services/auth.service';
import { PatientEngagementService } from '../../services/patient-engagement.service';
import { SMSservice } from '../../../../core/services/sms.service';
import { MetaData } from '../../models/user.model';
import { combineLatest, firstValueFrom, timeout } from 'rxjs';
import { filter, first } from 'rxjs/operators';

@Component({
  selector: 'app-callback',
  templateUrl: './callback.component.html',
  styleUrl: './callback.component.css'
})
export class CallbackComponent implements OnInit, OnDestroy {
  elinkData: any;
  elinkParams: any;
  isGuestUser = false;
  phInfo: any;
  user: any;
  pharmacyInfo: any;
  validMetaData: any;

  constructor(
    public auth: AuthService,
    public peAuthService: PeAuthService,
    private _patientEngagementService: PatientEngagementService,
    public _activeRoute: ActivatedRoute,
    public smsService: SMSservice
  ) {}

  ngOnInit() {
    // Cross-tab logout is handled by AppComponent's storage listener
    combineLatest([
      this.auth.isLoading$,
      this.auth.isAuthenticated$
    ]).pipe(
      filter(([loading, _]) => !loading),
      first(),
      timeout(10000) // Prevent infinite spinner if Auth0 SDK hangs
    ).subscribe({
      next: async ([_, isAuthenticated]) => {
        console.log('[CallbackComponent] Auth0 status:', { isAuthenticated });
        if (isAuthenticated) {
          // Save email in background (non-blocking) while handleAuthFlow runs
          this.auth.user$.pipe(first()).subscribe(user => {
            if (user?.email) {
              localStorage.setItem('lastLoginEmail', user.email);
            }
          });
          await this.handleAuthFlow();
        } else {
          console.log('[CallbackComponent] User is not authenticated!');
          await this.peAuthService.logout();
        }
      },
      error: async (err) => {
        console.error('[CallbackComponent] Auth0 timed out or errored, redirecting to login:', err);
        await this.peAuthService.logout();
      }
    });
  }

  ngOnDestroy() {
    // Reserved for future cleanup
  }

  async logoutAndRedirect(message?: string) {
    console.log('[CallbackComponent] logoutAndRedirect called:', { message });
    await this.peAuthService.logout();
  }

  safeJsonParse(val: string | null) {
    try {
      return val && val !== 'undefined' ? JSON.parse(val) : null;
    } catch (e) {
      return null;
    }
  }

  /** Main business logic flow for Auth0 callback handling */
  async handleAuthFlow() {
    try {
      // Parse localStorage values safely
      const eLinkDataStr = localStorage.getItem("eLinkData");
      const paramsDataStr = localStorage.getItem("paramsData");
      this.elinkData = this.safeJsonParse(eLinkDataStr);
      this.elinkParams = this.safeJsonParse(paramsDataStr);

      // Fetch token and store in localStorage so the interceptor can use it instantly
      try {
        const token = await firstValueFrom(
          this.auth.getAccessTokenSilently().pipe(timeout(5000))
        );
        if (token) {
          localStorage.setItem('access_token', JSON.stringify(token));
        }
      } catch (err) {
        console.warn('[CallbackComponent] Token pre-fetch timed out or failed, proceeding anyway');
      }
      this.isGuestUser = JSON.parse(localStorage.getItem('IsGuestUser') || 'false');

      // If we have params + eLinkData, proceed
      if (this.elinkData && this.elinkParams) {
        this.phInfo = {
          pharmacyNpi: this.elinkParams?.npi,
          patientId: this.elinkData?.EntityId
        };

        // Validate required data before proceeding
        if (this.phInfo.pharmacyNpi && this.phInfo.patientId) {
          localStorage.setItem('MetaData', JSON.stringify(this.phInfo));
          if (this.elinkData?.PatientPhoneNo) {
            console.log('[CallbackComponent] handleAuthFlow: PatientPhoneNo exists, calling patientPharmacyInfo');
            await this.smsService.patientPharmacyInfo();
          } else {
            console.log('[CallbackComponent] handleAuthFlow: PatientPhoneNo missing, fetching user by email.');
            try {
              const user = await firstValueFrom(this.auth.user$.pipe(first(), timeout(5000)));
              this.user = user;
              await this.isEmailuser(user?.email);
            } catch (err) {
              console.log('[CallbackComponent] handleAuthFlow: Error fetching user from Auth0');
              await this.logoutAndRedirect("Unable to fetch user information. Please try again.");
            }
          }
        } else {
          console.log('[CallbackComponent] handleAuthFlow: Invalid session or missing pharmacyNpi/patientId');
          await this.logoutAndRedirect("Invalid session or missing information. Please login again.");
        }
      } else {
        // Fallback: Try to get user by email
        console.log('[CallbackComponent] handleAuthFlow: Missing elinkData or elinkParams, falling back to user email');
        try {
          const user = await firstValueFrom(this.auth.user$.pipe(first(), timeout(5000)));
          this.user = user;
          await this.isEmailuser(user?.email);
        } catch (err) {
          console.log('[CallbackComponent] handleAuthFlow: Error fetching user from Auth0');
          await this.logoutAndRedirect("Unable to fetch user information. Please login again.");
        }
      }
    } catch (error) {
      console.log('[CallbackComponent] handleAuthFlow: Exception caught', error);
      await this.logoutAndRedirect("An error occurred. Please login again.");
    }
  }

  /** Looks up email, validates metaData, sets required localStorage, or logs out on failure */
  async isEmailuser(email: any) {
    const payload = {
      "patientEmail": email
    };

    try {
      const result = await firstValueFrom(this._patientEngagementService.postUserDetailEmail(payload));

      if (result) {
        this.validMetaData = result?.metaData?.metaData?.filter((data: MetaData) =>
          data?.npiData && data?.phoneNumber && data?.patientNo
        );

        if (this.validMetaData && this.validMetaData.length > 0) {
          const firstValidData = this.validMetaData[0];
          const params = {
            "id": "",
            "guid": "",
            "npi": firstValidData?.npiData,
          };
          localStorage.setItem('paramsData', JSON.stringify(params));

          const mobilePaymentSMSPayload = {
            "EntityId": firstValidData?.patientNo,
            "Entitytype": "Patient",
            "PatientPhoneNo": firstValidData?.phoneNumber,
            "TimeZoneOffset": -330,
            "PatientName": "",
            "PharmacyName": "",
            "PatientMessSettingId": null,
            "ElinkType": "General"
          };
          localStorage.setItem('eLinkData', JSON.stringify(mobilePaymentSMSPayload));

          if (firstValidData && firstValidData?.patientNo && firstValidData?.npiData) {
            this.phInfo = {
              "pharmacyNpi": firstValidData?.npiData,
              "patientId": firstValidData?.patientNo,
            };
            localStorage.setItem('MetaData', JSON.stringify(this.phInfo));

            console.log('[CallbackComponent] isEmailuser: Calling patientPharmacyInfo()');
            await this.smsService.patientPharmacyInfo();
          }
        } else {
          console.log('[CallbackComponent] isEmailuser: No valid records found, logging out');
          await this.logoutAndRedirect("No valid records found for your email. Please contact support.");
        }
      } else {
        console.log('[CallbackComponent] isEmailuser: No result from email lookup, logging out');
        await this.logoutAndRedirect("No valid records found for your email. Please contact support.");
      }
    } catch (error) {
      console.log('[CallbackComponent] isEmailuser: Error occurred:', error);
      await this.logoutAndRedirect("Unable to validate your email. Please try again.");
    }
  }
}
